/** Monument-icon frame + colourways.
 *
 * Every monument vector is a two-colour duotone: a stone hue and an
 * accent hue, each auto-shaded (highlights/shadows derived with
 * color-mix). The frame sets the CSS variables the icon bodies paint
 * with, so one geometry retints to any palette by swapping two colours.
 */
import type { CSSProperties, ReactNode } from "react";

export interface MonumentColors {
  stone: string;
  stoneShade: string;
  gold: string;
  goldShade: string;
}

/** The two approved colourways (client sign-off 2026-07). */
export const SAND_GOLD: MonumentColors = {
  stone: "#D8BE96",
  stoneShade: "#BFA06E",
  gold: "#D79A34",
  goldShade: "#B67F1F",
};

export const SALBEI_BRONZE: MonumentColors = {
  stone: "#D2DAC6",
  stoneShade: "#B2BEA0",
  gold: "#97A15C",
  goldShade: "#77813F",
};

export type ColorwayKey = "sandGold" | "salbeiBronze";

export const COLORWAYS: Record<ColorwayKey, MonumentColors> = {
  sandGold: SAND_GOLD,
  salbeiBronze: SALBEI_BRONZE,
};

/** CSS variables the icon bodies reference — two colours in, full clay
 * shading derived. */
export function monumentStyle(c: MonumentColors): CSSProperties {
  return {
    "--c1": c.stone,
    "--c1d": c.stoneShade,
    "--c2": c.gold,
    "--c2d": c.goldShade,
    // Punchy tonal range so the form reads even against a coloured wash.
    "--st-hi": "color-mix(in srgb, var(--c1), #fff 34%)",
    "--st": "var(--c1)",
    "--st-sh": "var(--c1d)",
    "--st-dp": "color-mix(in srgb, var(--c1d), #000 34%)",
    "--gd-hi": "color-mix(in srgb, var(--c2), #fff 38%)",
    "--gd": "var(--c2)",
    "--gd-sh": "var(--c2d)",
    "--gd-dp": "color-mix(in srgb, var(--c2d), #000 30%)",
  } as CSSProperties;
}

export function MonumentFrame({
  colors,
  title,
  className = "",
  children,
}: {
  colors: MonumentColors;
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 160 120"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
      style={monumentStyle(colors)}
    >
      {children}
    </svg>
  );
}
